import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PromptForm from './PromptForm';
import CampaignEditor from './CampaignEditor';
import CampaignList from './CampaignList';
import CampaignGenerationProgress from './CampaignGenerationProgress';
import AssetManager from './AssetManager';
import AdsManager from './Ads/AdsManager';
import WidgetsManager from './Widgets/WidgetsManager';
import InfoPlaques from './QrCodes/InfoPlaques';
import MenuItems from './QrCodes/MenuItems';
import MenusWorkspace from '../Menus/MenusWorkspace';
import PrintShop from './QrCodes/PrintShop';
import { claimArtworkGenerationRequest } from './artworkGenerationRequest';
import useCanvaConnection from '../../hooks/useCanvaConnection';
import { getUserRoleFromToken } from '../../utils/jwt';

export default function CreativeStudio({
  site,
  area,
  selectedDevice = null,
  requestedView = null,
  openPlaqueId = null,
  openWidgetId = null,
  generationRequest = null,
  campaignRequest = null,
  onOpenSourceGig = null,
  openRequestKey = 0,
}) {
  const artworkTimeoutRef = useRef(null);
  const claimedArtworkGenerationSignalsRef = useRef(new Set());
  const createInitialGenerationProgress = useCallback((autoGenerateArtwork) => ({
    active: true,
    isLocked: Boolean(autoGenerateArtwork),
    autoGenerateArtwork: Boolean(autoGenerateArtwork),
    panels: {
      image: {
        state: autoGenerateArtwork ? 'in_progress' : 'pending',
        label: autoGenerateArtwork ? 'Generating artwork...' : 'Artwork generation skipped.',
      },
      tv: { state: 'in_progress', label: 'Generating TV content...' },
      social: { state: 'in_progress', label: 'Generating social posts...' },
      announcement: { state: 'in_progress', label: 'Generating TTS...' },
      overlay: { state: 'in_progress', label: 'Generating overlay ads...' },
      landing: { state: 'in_progress', label: 'Generating landing page...' },
      advertising: { state: 'pending', label: 'Waiting for campaign content...' },
    },
  }), []);

  const [view, setView] = useState('create');
  const [widgetEditorRequest, setWidgetEditorRequest] = useState({ widgetId: null, requestKey: 0 });
  const [plaqueEditorRequest, setPlaqueEditorRequest] = useState({ plaqueId: null, requestKey: 0 });
  const [campaign, setCampaign] = useState(null);
  const [assets, setAssets] = useState([]);
  const [assetsReady, setAssetsReady] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignScope, setCampaignScope] = useState('current');
  const [autoGenerateSignal, setAutoGenerateSignal] = useState(0);
  const [autoSaveSignal, setAutoSaveSignal] = useState(0);
  const [announcementPreset, setAnnouncementPreset] = useState(null);
  const [generationProgress, setGenerationProgress] = useState(null);
  const [sourceContext, setSourceContext] = useState(null);
  const [preview, setPreview] = useState({
    open: false,
    asset: null,
    url: null,
    contentType: null,
    loading: false,
    error: null,
  });
  const previewUrlRef = useRef(null);
  const previewAbortRef = useRef(null);
  const [campaignCreatorOpen, setCampaignCreatorOpen] = useState(true);
  const [savedCampaignsOpen, setSavedCampaignsOpen] = useState(false);
  const { connected: canvaConnected, connect: connectCanva } = useCanvaConnection();
  const isAdmin = getUserRoleFromToken(localStorage.getItem('token')) === 'admin';
  const claimArtworkGenerationSignal = useCallback((signal) => {
    return claimArtworkGenerationRequest(claimedArtworkGenerationSignalsRef.current, signal);
  }, []);
  const sortedCampaigns = useMemo(() => {
    const toTimestamp = (value) => {
      if (!value) return 0;
      const time = new Date(value).getTime();
      return Number.isFinite(time) ? time : 0;
    };

    return [...campaigns].sort((a, b) => {
      const diff = toTimestamp(b.updated_at || b.updatedAt || b.created_at || b.createdAt)
        - toTimestamp(a.updated_at || a.updatedAt || a.created_at || a.createdAt);
      if (diff !== 0) return diff;
      return Number(b.id || 0) - Number(a.id || 0);
    });
  }, [campaigns]);
  const visibleCampaigns = useMemo(() => {
    if (campaignScope === 'accessible') return sortedCampaigns;
    const currentSiteId = Number(site?.id || 0) || null;
    if (!currentSiteId) return [];
    return sortedCampaigns.filter((campaignRow) => String(campaignRow?.siteId || '') === String(currentSiteId));
  }, [campaignScope, site?.id, sortedCampaigns]);

  const handleLanguagePreset = useCallback((preset) => {
    if (!preset) return;
    setAnnouncementPreset((prev) => {
      if (
        prev &&
        prev.language === preset.language &&
        (prev.voice || '') === (preset.voice || '')
      ) {
        return prev;
      }
      return { language: preset.language, voice: preset.voice || '' };
    });
  }, []);

  const handleGenerateStart = useCallback(({ autoGenerateArtwork, language }) => {
    setAnnouncementPreset({
      language: language?.announcementLanguage || 'en-AU',
      voice: language?.announcementVoice || '',
    });
    setGenerationProgress(createInitialGenerationProgress(Boolean(autoGenerateArtwork)));
    setCampaign(null);
  }, [createInitialGenerationProgress]);

  const handleGenerateError = useCallback(() => {
    if (artworkTimeoutRef.current) {
      window.clearTimeout(artworkTimeoutRef.current);
      artworkTimeoutRef.current = null;
    }
    setGenerationProgress((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        active: false,
        isLocked: false,
        panels: Object.fromEntries(
          Object.entries(prev.panels || {}).map(([key, panel]) => [
            key,
            {
              ...panel,
              state: panel.state === 'complete' ? 'complete' : 'error',
              label: panel.state === 'complete' ? panel.label : 'Generation failed.',
            },
          ])
        ),
      };
    });
  }, []);

  const markCopyGenerationComplete = useCallback((autoGenerateArtwork) => {
    setGenerationProgress((prev) => {
      const base = prev || createInitialGenerationProgress(Boolean(autoGenerateArtwork));
      return {
        ...base,
        active: Boolean(autoGenerateArtwork),
        isLocked: Boolean(autoGenerateArtwork),
        autoGenerateArtwork: Boolean(autoGenerateArtwork),
        panels: {
          ...base.panels,
          image: {
            state: autoGenerateArtwork ? 'in_progress' : 'pending',
            label: autoGenerateArtwork ? 'Generating artwork...' : 'Ready to choose artwork.',
          },
          tv: { state: 'complete', label: 'TV content generated.' },
          social: { state: 'complete', label: 'Social posts generated.' },
          announcement: { state: 'complete', label: 'TTS script generated.' },
          overlay: { state: 'complete', label: 'Overlay ad copy generated.' },
          landing: { state: 'complete', label: 'Landing page copy generated.' },
          advertising: { state: 'complete', label: 'Advertising panel ready to configure.' },
        },
      };
    });
  }, [createInitialGenerationProgress]);

  const handleArtworkGenerationStart = useCallback(() => {
    if (artworkTimeoutRef.current) {
      window.clearTimeout(artworkTimeoutRef.current);
    }
    if (typeof window !== 'undefined') {
      artworkTimeoutRef.current = window.setTimeout(() => {
        setGenerationProgress((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            active: false,
            isLocked: false,
            panels: {
              ...prev.panels,
              image: {
                state: 'error',
                label: 'Artwork generation timed out. You can open Artwork Chooser and try Generate New.',
              },
            },
          };
        });
        artworkTimeoutRef.current = null;
      }, 90000);
    }
    setGenerationProgress((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        active: true,
        isLocked: true,
        panels: {
          ...prev.panels,
          image: {
            state: 'in_progress',
            label: 'Generating artwork. This can take a little while...',
          },
        },
      };
    });
  }, []);

  const handleArtworkGenerationComplete = useCallback(() => {
    if (artworkTimeoutRef.current) {
      window.clearTimeout(artworkTimeoutRef.current);
      artworkTimeoutRef.current = null;
    }
    setGenerationProgress((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        active: false,
        isLocked: false,
        panels: {
          ...prev.panels,
          image: {
            state: 'complete',
            label: 'Artwork generated and selected.',
          },
        },
      };
    });
  }, []);

  const handleArtworkGenerationError = useCallback(() => {
    if (artworkTimeoutRef.current) {
      window.clearTimeout(artworkTimeoutRef.current);
      artworkTimeoutRef.current = null;
    }
    setGenerationProgress((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        active: false,
        isLocked: false,
        panels: {
              ...prev.panels,
              image: {
                state: 'error',
                label: 'Artwork generation failed. You can open Artwork Chooser and try Generate New.',
              },
            },
          };
        });
  }, []);

  useEffect(() => () => {
    if (artworkTimeoutRef.current) {
      window.clearTimeout(artworkTimeoutRef.current);
      artworkTimeoutRef.current = null;
    }
  }, []);

  const fetchAssets = useCallback(async () => {
    if (!site?.id) {
      setAssets([]);
      setAssetsReady(true);
      return [];
    }
    setAssetsReady(false);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`/api/creative/assets?siteId=${site.id}`, {
        cache: 'no-store',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        let announcements = [];
        try {
          const annRes = await fetch('/api/announcements', {
            cache: 'no-store',
            headers: { Authorization: `Bearer ${token}` },
          });
          if (annRes.ok) announcements = await annRes.json();
        } catch (e) {
          console.error('Failed to fetch announcements', e);
        }
        const annAssets = announcements.map((a) => ({
          id: a.id,
          type: 'announcement',
          title: a.title,
          campaignId: a.campaign_id,
          variation: a.variation_idx,
          files: [],
        }));
        const nextAssets = [...(data.assets || []), ...annAssets];
        setAssets(nextAssets);
        return nextAssets;
      }
      return [];
    } finally {
      setAssetsReady(true);
    }
  }, [site]);

  const fetchCampaigns = useCallback(async () => {
    if (!site?.id) {
      setCampaigns([]);
      return;
    }
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`/api/creative/campaigns?scope=accessible&siteId=${encodeURIComponent(site.id)}`, {
        cache: 'no-store',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        console.error('Failed to fetch campaigns', res.status, await res.text());
        return;
      }
      const data = await res.json().catch(() => ({}));
      setCampaigns(Array.isArray(data.campaigns) ? data.campaigns : []);
    } catch (err) {
      console.error('Error fetching campaigns', err);
    }
  }, [site?.id]);

  const cleanupPreviewUrl = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  };

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  useEffect(() => {
    const handler = (evt) => {
      const doneSiteId = evt?.detail?.siteId || null;
      if (doneSiteId && site?.id && String(doneSiteId) !== String(site.id)) return;
      fetchAssets();
    };
    window.addEventListener('uploadQueue:itemDone', handler);
    return () => window.removeEventListener('uploadQueue:itemDone', handler);
  }, [fetchAssets, site]);

  useEffect(() => () => {
    previewAbortRef.current?.abort();
    cleanupPreviewUrl();
  }, []);

  const handleCreateDesign = async (text, option) => {
    if (!canvaConnected) {
      connectCanva();
      return;
    }
    console.log('Requesting Canva design:', { text, option });
    const token = localStorage.getItem('token');
    const payload = { text };
    if (option.mode === 'preset') {
      payload.preset = { name: option.name };
    } else {
      payload.dimensions = { width: option.w, height: option.h };
    }
    try {
      const res = await fetch('/api/canva/designs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        console.error('Failed to create design', res.status, await res.text());
        return;
      }
      const result = await res.json();
      console.log('Received Canva response:', result);
      if (result.editUrl) window.open(result.editUrl, '_blank');
    } catch (err) {
      console.error('Error creating Canva design', err);
    }
  };

  const renderCreateSection = useCallback(({
    title,
    description,
    isOpen,
    onToggle,
    statusLabel = '',
    children,
  }) => (
    <section className="mb-4 rounded-2xl border border-white/10 bg-gray-900/70">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left"
        onClick={onToggle}
      >
        <div>
          <div className="text-lg font-semibold text-white">{title}</div>
          <div className="mt-1 text-sm text-gray-300">{description}</div>
        </div>
        <div className="shrink-0 text-right">
          {statusLabel ? (
            <div className="text-xs uppercase tracking-[0.18em] text-gray-400">{statusLabel}</div>
          ) : null}
          <div className="mt-1 text-sm font-semibold text-indigo-300">{isOpen ? 'Hide' : 'Show'}</div>
        </div>
      </button>
      {isOpen ? (
        <div className="border-t border-white/10 px-4 py-4">
          {children}
        </div>
      ) : null}
    </section>
  ), []);

  const openCampaign = async (c) => {
    setCampaign(c);
    setCampaignCreatorOpen(false);
    setSavedCampaignsOpen(false);
    setView('create');
    const campaignId = Number(c?.id || 0) || null;
    if (!campaignId) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`/api/creative/campaigns/${campaignId}`, {
        cache: 'no-store',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        console.error('Failed to load full campaign', res.status, await res.text().catch(() => ''));
        return;
      }
      const fullCampaign = await res.json().catch(() => null);
      if (fullCampaign) setCampaign(fullCampaign);
    } catch (err) {
      console.error('Error loading full campaign', err);
    }
  };

  const openWidgetEditor = useCallback((widgetId) => {
    if (!widgetId) return;
    setWidgetEditorRequest((prev) => ({
      widgetId,
      requestKey: prev.requestKey + 1,
    }));
    setView('widgets');
  }, []);

  useEffect(() => {
    if (requestedView) setView(requestedView);
  }, [requestedView, openRequestKey]);

  useEffect(() => {
    if (requestedView === 'widgets' && openWidgetId) openWidgetEditor(openWidgetId);
  }, [openRequestKey, openWidgetEditor, openWidgetId, requestedView]);

  useEffect(() => {
    if (!generationRequest) return;
    setSourceContext({
      sourceLabel: generationRequest.sourceLabel || 'Scheduler / Gigs',
      gigId: Number(generationRequest.gigId || 0) || null,
      gigTitle: generationRequest.gigTitle || '',
      performerNames: Array.isArray(generationRequest.performerNames) ? generationRequest.performerNames : [],
      contextTitle: generationRequest.contextTitle || '',
      contextBody: generationRequest.contextBody || '',
      sourceSummary: generationRequest.sourceSummary || '',
    });
    setView('create');
  }, [generationRequest, openRequestKey]);

  useEffect(() => {
    if (!campaignRequest?.id) return;
    let active = true;
    (async () => {
      await Promise.all([fetchAssets(), fetchCampaigns()]);
      if (!active) return;
      setCampaign(campaignRequest);
      setSourceContext({
        sourceLabel: 'Facebook Social Content',
        contextTitle: campaignRequest.title || 'Facebook campaign',
        contextBody: 'Artwork and copy were imported from the selected Facebook post.',
        performerNames: [],
      });
      setCampaignCreatorOpen(false);
      setSavedCampaignsOpen(false);
      setView('create');
    })();
    return () => { active = false; };
  }, [campaignRequest, fetchAssets, fetchCampaigns]);

  useEffect(() => {
    if (!openPlaqueId) return;
    setPlaqueEditorRequest((prev) => ({
      plaqueId: openPlaqueId,
      requestKey: prev.requestKey + 1,
    }));
    setView('plaques');
  }, [openPlaqueId, openRequestKey]);

  const getPreviewKind = (asset) => {
    if (!asset) return 'original';
    if (asset.previewKind) return asset.previewKind;
    if (asset.thumbnailKind) return asset.thumbnailKind;
    const files = Array.isArray(asset.files) ? asset.files : [];
    const priority = ['preview', 'original', 'video', 'audio'];
    for (const kind of priority) {
      const match = files.find((file) => (file.kind || '').toLowerCase() === kind);
      if (match) return kind;
    }
    return files.length ? (files[0].kind || 'original').toLowerCase() : 'original';
  };

  const handleAssetOpen = async (asset) => {
    if (asset.type === 'announcement') {
      const camp = campaigns.find((c) => c.id === asset.campaignId);
      if (camp) {
        setCampaign(camp);
        setCampaignCreatorOpen(false);
        setSavedCampaignsOpen(false);
        setView('create');
      }
      return;
    }

    if (!asset?.id) return;

    const token = localStorage.getItem('token');
    const previewKind = getPreviewKind(asset);
    cleanupPreviewUrl();
    previewAbortRef.current?.abort();
    const controller = new AbortController();
    previewAbortRef.current = controller;

    setPreview({
      open: true,
      asset,
      url: null,
      contentType: null,
      loading: true,
      error: null,
    });

    try {
      const res = await fetch(
        `/api/creative/assets/${asset.id}/files/${encodeURIComponent(previewKind)}`,
        {
          cache: 'no-store',
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        }
      );
      if (!res.ok) throw new Error(`Failed to load asset preview (${res.status})`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      previewUrlRef.current = objectUrl;
      const contentType = res.headers.get('Content-Type') || '';
      setPreview({
        open: true,
        asset,
        url: objectUrl,
        contentType,
        loading: false,
        error: null,
      });
    } catch (err) {
      if (controller.signal.aborted) return;
      console.error('Failed to open asset preview', err);
      setPreview({
        open: true,
        asset,
        url: null,
        contentType: null,
        loading: false,
        error: 'Failed to load asset preview.',
      });
    } finally {
      if (previewAbortRef.current === controller) {
        previewAbortRef.current = null;
      }
    }
  };

  const closePreview = () => {
    previewAbortRef.current?.abort();
    previewAbortRef.current = null;
    cleanupPreviewUrl();
    setPreview({
      open: false,
      asset: null,
      url: null,
      contentType: null,
      loading: false,
      error: null,
    });
  };

  const deleteAsset = async (asset) => {
    if (!asset) return;
    const token = localStorage.getItem('token');
    const endpoint = asset.type === 'announcement'
      ? `/api/announcements/${asset.id}`
      : `/api/creative/assets/${asset.id}`;
    try {
      const res = await fetch(endpoint, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        console.error('Failed to delete asset', res.status, await res.text());
        return;
      }
      if (preview.asset?.id === asset.id) {
        closePreview();
      }
      fetchAssets();
    } catch (err) {
      console.error('Error deleting asset', err);
    }
  };


  return (
    <div className="text-white bg-black min-h-screen">
      <div className="mb-4 rounded-2xl border border-white/10 bg-gray-900/70 p-4">
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-300">Creative Studio</div>
        <div className="mt-2 text-2xl font-semibold text-white">Campaign Creator</div>
        <div className="mt-1 text-sm text-gray-300">
          Scheduler gigs and standalone promotions both use this same campaign workflow.
        </div>
      </div>
      <div className="flex gap-4 mb-4">
        <button
          className={`px-3 py-1 rounded text-white ${view === 'create' ? 'bg-indigo-600' : 'bg-gray-700'}`}
          onClick={() => setView('create')}
        >
          Campaign Creator
        </button>
        {isAdmin && (
          <button
            className={`px-3 py-1 rounded text-white ${view === 'ads' ? 'bg-indigo-600' : 'bg-gray-700'}`}
            onClick={() => setView('ads')}
          >
            Ads
          </button>
        )}
        {isAdmin && (
          <button
            className={`px-3 py-1 rounded text-white ${view === 'widgets' ? 'bg-indigo-600' : 'bg-gray-700'}`}
            onClick={() => setView('widgets')}
          >
            Widgets
          </button>
        )}
        <button
          className={`px-3 py-1 rounded text-white ${view === 'assets' ? 'bg-indigo-600' : 'bg-gray-700'}`}
          onClick={() => setView('assets')}
        >
          Asset Browser
        </button>
        <button
          className={`px-3 py-1 rounded text-white ${view === 'plaques' ? 'bg-indigo-600' : 'bg-gray-700'}`}
          onClick={() => setView('plaques')}
        >
          Info Plaques
        </button>
        <button
          className={`px-3 py-1 rounded text-white ${view === 'menu-qr' ? 'bg-indigo-600' : 'bg-gray-700'}`}
          onClick={() => setView('menu-qr')}
        >
          Menu QRs
        </button>
        <button
          className={`px-3 py-1 rounded text-white ${view === 'product-qr' ? 'bg-indigo-600' : 'bg-gray-700'}`}
          onClick={() => setView('product-qr')}
        >
          Product QRs
        </button>
        <button
          className={`px-3 py-1 rounded text-white ${view === 'service-qr' ? 'bg-indigo-600' : 'bg-gray-700'}`}
          onClick={() => setView('service-qr')}
        >
          Service QRs
        </button>
        <button
          className={`px-3 py-1 rounded text-white ${view === 'print-shop' ? 'bg-indigo-600' : 'bg-gray-700'}`}
          onClick={() => setView('print-shop')}
        >
          Print Shop
        </button>
      </div>
      {view === 'create' && (
        <>
          {campaign && (
            <CampaignEditor
              campaign={campaign}
              site={site}
              selectedDevice={selectedDevice}
              assets={assets}
              assetsReady={assetsReady}
              onCreateDesign={handleCreateDesign}
              onSaved={(updated) => {
                setCampaign(updated);
                fetchAssets();
                fetchCampaigns();
              }}
              onDeleted={() => {
                setCampaign(null);
                setCampaignCreatorOpen(true);
                setSavedCampaignsOpen(false);
                fetchAssets();
                fetchCampaigns();
              }}
              onAnnouncementSaved={fetchAssets}
              onOverlaySaved={fetchAssets}
              onRefreshAssets={fetchAssets}
              autoGenerateSignal={autoGenerateSignal}
              claimArtworkGenerationSignal={claimArtworkGenerationSignal}
              autoSaveSignal={autoSaveSignal}
              announcementPreset={announcementPreset}
              generationProgress={generationProgress}
              onArtworkGenerationStart={handleArtworkGenerationStart}
              onArtworkGenerationComplete={handleArtworkGenerationComplete}
              onArtworkGenerationError={handleArtworkGenerationError}
            />
          )}
          {renderCreateSection({
            title: 'Campaign Creator',
            description: 'Create the campaign copy that feeds TV, social, overlays, announcements, and landing pages.',
            isOpen: campaignCreatorOpen,
            onToggle: () => setCampaignCreatorOpen((prev) => !prev),
            statusLabel: campaign ? 'Ready to edit or generate more' : 'Start here',
            children: (
              <>
                {sourceContext ? (
                  <div className="mb-4 rounded-2xl border border-indigo-500/30 bg-indigo-500/10 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-200">
                          From {sourceContext.sourceLabel}
                        </div>
                        <div className="mt-2 text-lg font-semibold text-white">
                          {sourceContext.contextTitle || 'Campaign Creator is using a published gig brief'}
                        </div>
                        <div className="mt-1 text-sm text-indigo-100">
                          {sourceContext.contextBody || 'This campaign was started from a published gig in Scheduler.'}
                        </div>
                        {sourceContext.performerNames.length ? (
                          <div className="mt-2 text-sm text-indigo-100">
                            Performers: {sourceContext.performerNames.join(', ')}
                          </div>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {typeof onOpenSourceGig === 'function' && sourceContext.gigId ? (
                          <button
                            type="button"
                            onClick={() => onOpenSourceGig(sourceContext.gigId)}
                            className="rounded-lg bg-white/10 px-3 py-2 text-sm font-semibold text-white hover:bg-white/20"
                          >
                            Back to Gig
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => setSourceContext(null)}
                          className="rounded-lg bg-indigo-950/60 px-3 py-2 text-sm font-semibold text-indigo-100 hover:bg-indigo-900/80"
                        >
                          Clear Source
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null}
                <PromptForm
                  siteId={site?.id}
                  site={site}
                  area={area}
                  showHeader={false}
                  onGenerateStart={handleGenerateStart}
                  onGenerateError={handleGenerateError}
                  generationRequest={generationRequest}
                  requestKey={openRequestKey}
                  onGenerate={async (data, options = {}) => {
                    setCampaign(data);
                    setCampaignCreatorOpen(true);
                    setSavedCampaignsOpen(false);
                    markCopyGenerationComplete(Boolean(options.autoGenerateArtwork));
                    await Promise.all([
                      fetchAssets(),
                      fetchCampaigns(),
                    ]);
                    setAutoSaveSignal((prev) => prev + 1);
                    if (options.autoGenerateArtwork) {
                      setAutoGenerateSignal((prev) => prev + 1);
                    }
                  }}
                  onLanguageSelected={handleLanguagePreset}
                />
                {!campaign && generationProgress ? (
                  <div className="mt-4">
                    <CampaignGenerationProgress progress={generationProgress} />
                  </div>
                ) : null}
              </>
            ),
          })}
          {renderCreateSection({
            title: 'Saved Campaigns',
            description: 'Newest first. Site ownership is shown on each campaign.',
            isOpen: savedCampaignsOpen,
            onToggle: () => setSavedCampaignsOpen((prev) => !prev),
            statusLabel: `${visibleCampaigns.length} total`,
            children: (
              <CampaignList
                campaigns={visibleCampaigns}
                currentSiteId={site?.id || null}
                currentSiteName={site?.name || ''}
                scope={campaignScope}
                onScopeChange={setCampaignScope}
                onOpen={openCampaign}
                showHeader={false}
              />
            ),
          })}
        </>
      )}
      {view === 'assets' && (
        <AssetManager
          siteId={site?.id}
          onOpenPreview={handleAssetOpen}
          onRefreshAssets={fetchAssets}
        />
      )}
      {view === 'ads' && isAdmin && (
        <AdsManager siteId={site?.id} />
      )}
      {view === 'widgets' && isAdmin && (
        <WidgetsManager
          openWidgetId={widgetEditorRequest.widgetId}
          openRequestKey={widgetEditorRequest.requestKey}
        />
      )}
      {view === 'plaques' && (
        <InfoPlaques
          site={site}
          area={area}
          assets={assets}
          onRefreshAssets={fetchAssets}
          onEditWidget={openWidgetEditor}
          openPlaqueId={plaqueEditorRequest.plaqueId}
          openRequestKey={plaqueEditorRequest.requestKey}
        />
      )}
      {view === 'menu-qr' && (
        <MenusWorkspace
          site={site}
          area={area}
          onEditWidget={openWidgetEditor}
        />
      )}
      {view === 'product-qr' && (
        <MenuItems
          site={site}
          area={area}
          assets={assets}
          onRefreshAssets={fetchAssets}
          catalogType="products"
          onEditWidget={openWidgetEditor}
        />
      )}
      {view === 'service-qr' && (
        <MenuItems
          site={site}
          area={area}
          assets={assets}
          onRefreshAssets={fetchAssets}
          catalogType="services"
          onEditWidget={openWidgetEditor}
        />
      )}
      {view === 'print-shop' && (
        <PrintShop
          site={site}
          area={area}
          assets={assets}
          onRefreshAssets={fetchAssets}
        />
      )}
      {preview.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4"
          onClick={closePreview}
        >
          <div
            className="relative bg-gray-900 rounded-lg p-4 w-full max-w-4xl max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-2 right-2 text-gray-300 hover:text-white text-2xl leading-none"
              onClick={closePreview}
              aria-label="Close preview"
            >
              ×
            </button>
            <h3 className="text-lg font-semibold mb-4">
              {preview.asset?.variant_key || preview.asset?.title || 'Asset preview'}
            </h3>
            {preview.loading && <div className="text-gray-300">Loading preview...</div>}
            {preview.error && <div className="text-red-400">{preview.error}</div>}
            {!preview.loading && !preview.error && preview.url && (
              <div className="flex justify-center">
                {preview.contentType?.startsWith('image/') ? (
                  <img
                    src={preview.url}
                    alt="Asset preview"
                    className="max-h-[70vh] rounded-md object-contain"
                  />
                ) : preview.contentType?.startsWith('video/') ? (
                  <video
                    src={preview.url}
                    controls
                    className="max-h-[70vh] rounded-md"
                  />
                ) : preview.contentType?.startsWith('audio/') ? (
                  <audio src={preview.url} controls className="w-full" />
                ) : (
                  <a
                    href={preview.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-indigo-400 underline"
                  >
                    Open asset in new tab
                  </a>
                )}
              </div>
            )}
            {!preview.loading && !preview.error && !preview.url && (
              <div className="text-gray-400">Preview not available for this asset.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
